// QUE FAMILIAS CAMBIAN DE RESPONSABLE AL UNIFICAR
//
// Unificar dos personas mueve sus vinculos de familia a la ficha que se conserva. Al
// mover un vinculo PRINCIPAL, el trigger trg_vinculo_espejo_familia reescribe
// la persona principal de la familia, y de ahi sale el destino del OTP de consentimiento (consent.js
// resuelve el telefono de la persona principal). Es decir: unificar puede mandar el codigo de una
// familia a otro telefono.
//
// Eso es lo CORRECTO -- es exactamente lo que el usuario autoriza al unificar. Pero
// tiene que saberlo ANTES, no despues. Esta funcion arma esa advertencia.
//
// Vive fuera del componente por la misma razon que responsableResolver: es la unica
// decision de la pantalla que se puede equivocar en silencio, y aqui tiene tests.

// La familia NO cambia de responsable si la ficha que se conserva ya era la principal
// ahi: el vinculo del origen se absorbe en el que ya existia.
export const familiasQueCambianAlUnificar = (personas = [], destinoId = null) => {
  if (!destinoId) return []
  const lista = Array.isArray(personas) ? personas : []
  const destino = lista.find((p) => p?.id === destinoId)

  const yaPrincipal = new Set(
    (destino?.vinculos || [])
      .filter((v) => v?.rol === 'responsable' && v?.es_principal)
      .map((v) => v.familia_id)
  )

  const nombres = []
  lista
    .filter((p) => p?.id && p.id !== destinoId)
    .forEach((p) => {
      ;(p.vinculos || [])
        .filter((v) => v?.rol === 'responsable' && v?.es_principal && !yaPrincipal.has(v.familia_id))
        .forEach((v) => {
          const nombre = String(v.familia || '').trim() || 'Familia sin nombre'
          if (!nombres.includes(nombre)) nombres.push(nombre)
        })
    })
  return nombres
}

// QUE MENORES CAMBIAN DE TUTOR AL UNIFICAR
//
// El otro eje, y el que pesa mas: `familia_personas` es de pagos, `jugador_personas` es
// de representacion legal. Desde 20260828d unificar mueve LOS DOS, pero la advertencia
// previa solo hablaba de familias -- asi que cambiar quien representa legalmente a N
// menores pasaba sin decirse. Es el mismo error que esta funcion existe para no repetir.
//
// El menor NO cambia de tutor si la ficha que se conserva ya lo era: el vinculo de la
// origen se absorbe en el que ya existia (ON CONFLICT DO UPDATE en el RPC).
export const menoresQueCambianAlUnificar = (personas = [], destinoId = null) => {
  if (!destinoId) return []
  const lista = Array.isArray(personas) ? personas : []
  const destino = lista.find((p) => p?.id === destinoId)

  const yaTutor = new Set((destino?.tutelas || []).map((t) => t?.jugador_id).filter(Boolean))

  const nombres = []
  lista
    .filter((p) => p?.id && p.id !== destinoId)
    .forEach((p) => {
      ;(p.tutelas || [])
        .filter((t) => t?.jugador_id && !yaTutor.has(t.jugador_id))
        .forEach((t) => {
          const nombre = String(t.jugador || '').trim() || 'Jugador sin nombre'
          if (!nombres.includes(nombre)) nombres.push(nombre)
        })
    })
  return nombres
}

// QUE CONTACTO SOBREVIVE AL UNIFICAR
//
// Espejo exacto de la regla del RPC (20260829): lo que ya tiene el destino manda, y sus
// huecos se rellenan con lo que traigan las origenes, tomando la primera por
// (nombre_completo, id). Nunca se pisa un dato bueno con otro dato bueno.
//
// Vive aqui, con pruebas, porque el confirm le dice al usuario QUE telefono y QUE correo
// van a quedar -- y de ahi salen los cobros y el codigo de consentimiento. Si esta
// funcion y el RPC se separan, la pantalla miente.
const primerValor = (personas, campo) => {
  const ordenadas = [...personas].sort((a, b) => {
    const dif = String(a?.nombre_completo || '').localeCompare(String(b?.nombre_completo || ''))
    return dif !== 0 ? dif : String(a?.id || '').localeCompare(String(b?.id || ''))
  })
  const con = ordenadas.find((p) => String(p?.[campo] || '').trim())
  return con ? { valor: String(con[campo]).trim(), de: con.nombre_completo || '' } : null
}

export const contactoQueSobrevive = (personas = [], destinoId = null) => {
  const lista = (Array.isArray(personas) ? personas : []).filter((p) => p?.id)
  const destino = lista.find((p) => p.id === destinoId)
  if (!destino) return { telefono: null, email: null }
  const origenes = lista.filter((p) => p.id !== destinoId)

  const resolver = (campo) => {
    const propio = String(destino[campo] || '').trim()
    if (propio) return { valor: propio, de: destino.nombre_completo || '', heredado: false }
    const traido = primerValor(origenes, campo)
    return traido ? { ...traido, heredado: true } : null
  }

  return { telefono: resolver('telefono'), email: resolver('email') }
}

// Cual ficha proponer conservar: la del nombre mas completo. Entre "Erika Barrios G."
// y "Erika Barrios Godinez" gana la segunda. Es una PROPUESTA -- la regla 5 dice que
// nada se decide solo, y el usuario puede elegir otra.
//
// El desempate por id evita que la propuesta cambie sola entre dos cargas cuando dos
// nombres miden lo mismo.
export const fichaQueSeConserva = (personas = []) => {
  const lista = (Array.isArray(personas) ? personas : []).filter((p) => p?.id)
  if (!lista.length) return null
  return [...lista].sort((a, b) => {
    const dif = String(b.nombre_completo || '').trim().length - String(a.nombre_completo || '').trim().length
    return dif !== 0 ? dif : String(a.id).localeCompare(String(b.id))
  })[0].id
}

// DE QUIEN HABLA CADA FILA
//
// La columna del medio mostraba `detalle`, que es la explicacion de la CATEGORIA: las
// once filas de "Sin familia" decian el mismo texto, y en la lista hay dos "Familia
// Salazar" sin forma de saber cual es cual. El usuario lo dijo asi: "no se sabe de que
// hablas o de cual".
//
// Es la misma leccion del nombre de la familia (c517ecb): un nombre no identifica a
// nadie. Lo que identifica es el contexto -- sus jugadores, su telefono, lo que su
// ficha ya dice. Casi todo eso ya venia en `datos`; faltaba mostrarlo.
//
// La explicacion de la categoria NO se pierde: vive en la columna "Categoria" y en los
// filtros de arriba. Aqui es ademas el ultimo recurso, para que ninguna fila quede en
// blanco ni imprima "undefined".
//
// Vive fuera del JSX por lo mismo que las de arriba: es logica, y en este bloque la
// logica sin prueba es justo la que se rompio siete veces.
const limpio = (v) => String(v ?? '').trim()

const unir = (partes, sep) => partes.map(limpio).filter(Boolean).join(sep)

// Sin repetidos y sin vacios, conservando el orden en el que vinieron (la consulta ya
// los ordena por nombre). Un responsable de dos familias con el mismo hijo en las dos
// no tiene por que aparecer dos veces.
const listaUnica = (xs) => {
  const out = []
  ;(Array.isArray(xs) ? xs : []).forEach((x) => {
    const v = limpio(x)
    if (v && !out.includes(v)) out.push(v)
  })
  return out
}

// Los jugadores de todas las familias de una persona. `vinculos[].jugadores` lo trae la
// consulta desde 20260830: el NOMBRE de la familia no sirve para identificarla -- se
// calculo una vez con la heuristica del apellido materno y quedo fosilizado.
const jugadoresDeVinculos = (vinculos) =>
  listaUnica([].concat(...(Array.isArray(vinculos) ? vinculos : []).map((v) => v?.jugadores || [])))

const menoresDeTutelas = (tutelas) =>
  listaUnica((Array.isArray(tutelas) ? tutelas : []).map((t) => t?.jugador))

const nombreDePersona = (p) =>
  limpio(p?.nombre_completo) || unir([p?.nombres, p?.apellidos], ' ') || 'Sin nombre'

const conJugadores = (nombre, jugadores) =>
  jugadores.length ? `${nombre} (${jugadores.join(', ')})` : nombre

const CONSTRUCTORES = {
  // "871 411 2547 · responsable de Fernanda Sánchez". El telefono es lo que distingue a
  // dos "ADRIANA", y los jugadores son lo que distingue a dos telefonos vacios.
  responsable_sin_apellido: (d) => {
    const jugadores = jugadoresDeVinculos(d.vinculos)
    const menores = menoresDeTutelas(d.tutelas)
    const quien = jugadores.length
      ? `responsable de ${jugadores.join(', ')}`
      : menores.length ? `tutor de ${menores.join(', ')}` : ''
    return unir([limpio(d.telefono) || limpio(d.email), quien], ' · ')
  },

  // "Jesus Antonio (Diego, Ana) · Jesus ramirez (Luis)". Antes decia solo los dos
  // nombres, que en un grupo de telefono compartido son casi siempre el mismo apellido.
  persona_telefono_duplicado: (d) =>
    (Array.isArray(d.personas) ? d.personas : [])
      .map((p) => conJugadores(nombreDePersona(p), jugadoresDeVinculos(p?.vinculos)))
      .join(' · '),

  // "Gilberto Matías Salazar": los jugadores son lo unico que separa a dos "Familia
  // Salazar". Si no tiene ninguno se dice, que ademas es el dato con el que se decide
  // borrarla.
  familia_sin_responsable: (d) => {
    const jugadores = listaUnica(d.jugadores)
    return jugadores.length ? jugadores.join(' · ') : 'Sin jugadores registrados'
  },

  // "Su ficha dice: Mamá · 8712772531". Si la ficha ya dice quien responde, eso es
  // media pista para asignarle familia; y si no dice nada, eso tambien hay que saberlo.
  jugador_sin_familia: (d) => {
    const dice = unir([d.contacto, d.telefono_contacto], ' · ')
    return dice ? `Su ficha dice: ${dice}` : 'Sin responsable en la ficha'
  },
}

// `firma_difiere_de_ficha` no tiene constructor a proposito: su `detalle` ya nombra al
// firmante, su correo y lo que dice la ficha. Es el unico que ya hablaba de SU fila.
export const detalleDeFicha = (ficha) => {
  if (!ficha) return ''
  const construir = CONSTRUCTORES[ficha.tipo]
  const texto = construir ? limpio(construir(ficha.datos || {})) : ''
  return texto || limpio(ficha.detalle)
}
